import { convertPeToUpdateReq } from '../converter/converter';
import { ProjectElementData } from '../dto/EntityRepository';
import {
  UpdateDocumentReq,
  UpdateFormationItemReq,
  UpdatePdfAssetReq,
  UpdatePEWorkReq,
  UpdateProjectElementBulkInfoReq,
  UpdateProjectElementReq,
  UpdateTextBlockReq,
  UpdateUploadFileReq,
  UpdateWorkDetailReq,
} from '../dto/ReqDtoRepository';
import { ProjectElementType, ServiceType, TextBlockType } from '../enum/EnumRepository';
import { isBase64 } from './findAdjustImg';

export type UploadPEImageFn = <T extends {
  id: string;
  updateUploadFileReq: UpdateUploadFileReq;
  workId?: string;
  formationId?: string;
}>(
  serviceType: ServiceType,
  data: T,
  projectId?: string,
) => Promise<T | null>;

export type UploadPEPdfFn = (
  serviceType: ServiceType,
  data: UpdatePdfAssetReq,
  projectId?: string,
) => Promise<UpdatePdfAssetReq | null>;

/**
 * 단건 PE 저장과 동일: convert 후 imageChanged인 파일만 S3 업로드.
 * 업로드 실패 시 null.
 */
export const prepareProjectElementUpdateReq = async (
  data: ProjectElementData,
  projectId: string,
  uploadPEImage: UploadPEImageFn,
  uploadPEPdf: UploadPEPdfFn,
): Promise<UpdateProjectElementReq | null> => {
  let updateDto: UpdateProjectElementReq = convertPeToUpdateReq(data);
  const hasTextBlockImageChanged =
    data.projectElementType === ProjectElementType.TEXT &&
    data.selectedTextBlocks.some((tb) => tb.type === TextBlockType.IMAGE && tb.imageChanged);
  const hasFormationSlotImageChanged =
    data.projectElementType === ProjectElementType.FORMATION &&
    data.formation.items.some((item) => item.imageChanged);

  if (!data.imageChanged && !hasTextBlockImageChanged && !hasFormationSlotImageChanged) {
    return updateDto;
  }

  switch (updateDto.projectElementType) {
    case ProjectElementType.WORK: {
      const afterUploadImage = await uploadPEImage(
        ServiceType.WORK,
        updateDto.updateWorkReq,
      );
      if (!afterUploadImage) return null;
      updateDto.updateWorkReq = afterUploadImage as UpdatePEWorkReq;
      break;
    }
    case ProjectElementType.DETAIL: {
      const afterUploadDetailImage = await uploadPEImage(
        ServiceType.DETAIL,
        updateDto.updateWorkDetailReq,
      );
      if (!afterUploadDetailImage) return null;
      updateDto.updateWorkDetailReq = afterUploadDetailImage as UpdateWorkDetailReq;
      break;
    }
    case ProjectElementType.DOCUMENT: {
      const afterUploadDocImage = await uploadPEImage(
        ServiceType.DOCUMENT,
        updateDto.updateDocumentReq,
        projectId,
      );
      if (!afterUploadDocImage) return null;
      updateDto.updateDocumentReq = afterUploadDocImage as UpdateDocumentReq;
      break;
    }
    case ProjectElementType.PDF: {
      const afterUploadPdf = await uploadPEPdf(
        ServiceType.PDF,
        updateDto.updatePdfAssetReq,
        projectId,
      );
      if (!afterUploadPdf) return null;
      updateDto.updatePdfAssetReq = afterUploadPdf;
      break;
    }
    case ProjectElementType.TEXT: {
      if (!updateDto.updateTextReq.textBlocks) break;

      const uploadedTextBlocks: UpdateTextBlockReq[] = [];
      for (const textBlockReq of updateDto.updateTextReq.textBlocks) {
        const sourceBlock = data.projectElementType === ProjectElementType.TEXT
          ? data.selectedTextBlocks.find((tb) => tb.id === textBlockReq.id)
          : undefined;

        if (sourceBlock?.type === TextBlockType.IMAGE && sourceBlock.imageChanged) {
          const uploaded = await uploadPEImage(
            ServiceType.TEXT_BLOCK,
            textBlockReq,
            updateDto.updateTextReq.id,
          );
          if (!uploaded) return null;
          uploadedTextBlocks.push(uploaded);
        } else {
          uploadedTextBlocks.push(textBlockReq);
        }
      }
      updateDto.updateTextReq.textBlocks = uploadedTextBlocks;
      break;
    }
    case ProjectElementType.FORMATION: {
      const formationItems = updateDto.updateFormationReq.items;
      if (formationItems.length === 0) break;

      const uploadedItems: UpdateFormationItemReq[] = [];
      for (const itemReq of formationItems) {
        const sourceItem = data.projectElementType === ProjectElementType.FORMATION
          ? data.formation.items.find((item) => (
            item.id === itemReq.id || item.slotIndex === itemReq.slotIndex
          ))
          : undefined;

        if (
          sourceItem?.imageChanged &&
          itemReq.updateUploadFileReq &&
          isBase64(itemReq.updateUploadFileReq.originUrl)
        ) {
          const uploaded = await uploadPEImage(
            ServiceType.FORMATION,
            {
              id: itemReq.id ?? `formation-slot-${itemReq.slotIndex ?? 0}`,
              updateUploadFileReq: itemReq.updateUploadFileReq,
              formationId: updateDto.updateFormationReq.id,
            },
            projectId,
          );
          if (!uploaded) return null;
          uploadedItems.push({
            ...itemReq,
            updateUploadFileReq: uploaded.updateUploadFileReq,
          });
        } else {
          uploadedItems.push(itemReq);
        }
      }
      updateDto.updateFormationReq.items = uploadedItems;
      break;
    }
    default:
      break;
  }

  return updateDto;
};

/** hasChanged인 항목만 모아 업로드까지 반영. 없거나 업로드 실패면 null. */
export const toUpdateProjectElementBulkInfoReq = async (
  projectId: string,
  projectElementList: ProjectElementData[],
  uploadPEImage: UploadPEImageFn,
  uploadPEPdf: UploadPEPdfFn,
): Promise<UpdateProjectElementBulkInfoReq | null> => {
  const changed = projectElementList.filter((pe) => pe.hasChanged);
  if (changed.length === 0) return null;

  const peList: UpdateProjectElementReq[] = [];
  for (const pe of changed) {
    const prepared = await prepareProjectElementUpdateReq(
      pe,
      projectId,
      uploadPEImage,
      uploadPEPdf,
    );
    if (!prepared) return null;
    peList.push(prepared);
  }
  return { projectId, peList };
};
